"use client";

import React, { createContext, useContext, useState, useCallback, useMemo } from "react";
import { FooterVariant, FooterContent, FooterLoadingState } from "./types";

// ============================================================
// FOOTER CONTEXT - State management for footer system
// ============================================================

interface FooterContextType {
  // Current footer state
  currentVariant: FooterVariant;
  content: FooterContent;

  // Loading state
  loadingState: FooterLoadingState;

  // Actions
  setVariant: (variant: FooterVariant) => void;
  updateContent: (updates: Partial<FooterContent>) => void;
  resetContent: () => void;
  setLoading: (isLoading: boolean) => void;
  setError: (error: Error | null) => void;

  // Bulk operations
  resetFooter: (variant: FooterVariant) => void;
}

const FooterContext = createContext<FooterContextType | undefined>(undefined);

// Default content for initial state
const defaultContent: FooterContent = {
  variant: "corporate",
  companyName: "INSTACKS",
  copyright: "© 2026 INSTACKS. All rights reserved.",
};

// ============================================================
// PROVIDER COMPONENT
// ============================================================

interface FooterProviderProps {
  children: React.ReactNode;
  initialVariant?: FooterVariant;
  initialContent?: FooterContent;
}

export const FooterProvider: React.FC<FooterProviderProps> = ({
  children,
  initialVariant = "corporate",
  initialContent,
}) => {
  const [currentVariant, setCurrentVariant] = useState<FooterVariant>(initialVariant);
  const [content, setContent] = useState<FooterContent>(initialContent || defaultContent);
  const [loadingState, setLoadingState] = useState<FooterLoadingState>({
    isLoading: false,
    error: null,
  });

  // Update variant and optionally reset content
  const setVariant = useCallback((variant: FooterVariant) => {
    setCurrentVariant(variant);
    setContent((prev) => ({
      ...prev,
      variant,
    }));
  }, []);

  // Partial content update
  const updateContent = useCallback((updates: Partial<FooterContent>) => {
    setContent((prev) => ({
      ...prev,
      ...updates,
    }));
  }, []);

  // Reset to default content for current variant
  const resetContent = useCallback(() => {
    // Import dynamically to avoid circular dependency
    import("./types").then(({ getDefaultFooterContent }) => {
      const defaults = getDefaultFooterContent(currentVariant);
      setContent(defaults);
    });
  }, [currentVariant]);

  // Set loading state
  const setLoading = useCallback((isLoading: boolean) => {
    setLoadingState((prev) => ({ ...prev, isLoading }));
  }, []);

  // Set error state
  const setError = useCallback((error: Error | null) => {
    setLoadingState((prev) => ({ ...prev, error }));
  }, []);

  // Reset footer with new variant
  const resetFooter = useCallback((variant: FooterVariant) => {
    setCurrentVariant(variant);
    import("./types").then(({ getDefaultFooterContent }) => {
      const defaults = getDefaultFooterContent(variant);
      setContent(defaults);
    });
  }, []);

  const value = useMemo(
    () => ({
      currentVariant,
      content,
      loadingState,
      setVariant,
      updateContent,
      resetContent,
      setLoading,
      setError,
      resetFooter,
    }),
    [
      currentVariant,
      content,
      loadingState,
      setVariant,
      updateContent,
      resetContent,
      setLoading,
      setError,
      resetFooter,
    ]
  );

  return (
    <FooterContext.Provider value={value}>
      {children}
    </FooterContext.Provider>
  );
};

// ============================================================
// HOOK
// ============================================================

export const useFooterContext = (): FooterContextType => {
  const context = useContext(FooterContext);
  if (!context) {
    throw new Error("useFooterContext must be used within a FooterProvider");
  }
  return context;
};

// ============================================================
// SELECTOR HOOKS (for optimized re-renders)
// ============================================================

export const useCurrentVariant = (): FooterVariant => {
  const context = useContext(FooterContext);
  return context?.currentVariant || "corporate";
};

export const useFooterContent = (): FooterContent => {
  const context = useContext(FooterContext);
  return context?.content || defaultContent;
};

export const useFooterLoading = (): FooterLoadingState => {
  const context = useContext(FooterContext);
  return (
    context?.loadingState || {
      isLoading: false,
      error: null,
    }
  );
};

export default FooterContext;
